import React, {useCallback, useMemo, useState} from 'react';
import {Paper} from '@mui/material';
import {
    ComplianceCheckTaskButtons,
    ComplianceCheckTaskCard,
    DefaultTaskButtons,
    DefaultTaskCard,
    EntityRecognitionTaskCard,
    StatementTaskCard,
} from './taskCards';
import {insertText} from '../../../../../utils/taskHelpers';

export default function ImprovementsTaskCard({
                                                 task,
                                                 editorView,
                                                 canNavigate,
                                                 onNavigate,
                                                 onAccept,
                                                 onReject,
                                                 onSelectAnotherEntity,
                                             }) {
    const [busy, setBusy] = useState(false);

    const disabledReason = useMemo(() => {
        if (!task?.id) return 'Missing task id';
        return null;
    }, [task]);

    const handleAccept = useCallback(async (e) => {
        e?.stopPropagation();
        if (busy) return;
        setBusy(true);
        try { await onAccept?.(task); } finally { setBusy(false); }
    }, [busy, onAccept, task]);

    const handleReject = useCallback(async (e) => {
        e?.stopPropagation();
        if (busy) return;
        setBusy(true);
        try { await onReject?.(task); } finally { setBusy(false); }
    }, [busy, onReject, task]);

    const handleSelectAnotherEntity = useCallback((e) => {
        e?.stopPropagation();
        if (busy) return;
        onSelectAnotherEntity?.(task);
    }, [busy, onSelectAnotherEntity, task]);

    const handleInsertText = useCallback(async (t, blockNode) => {
        if (busy) return;
        setBusy(true);
        try { await insertText(editorView, t); } catch (err) {
            console.error('Failed to insert text:', err);
        } finally { setBusy(false); }
    }, [busy, editorView]);

    const handleAcceptCompliance = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try { await onAccept?.(task); } finally { setBusy(false); }
    }, [busy, onAccept, task]);

    const isEntity = task?.taskType === 'entity_recognition';
    const isCompliance = task?.taskType === 'compliance_check' || task?.taskType === 'best_practices_check';

    /* Entity cards are self-contained — no Paper wrapper, card owns its own border styling */
    if (isEntity) {
        return (
            <EntityRecognitionTaskCard
                task={task}
                busy={busy}
                disabledReason={disabledReason}
                canNavigate={canNavigate}
                onNavigate={onNavigate}
                onApply={handleAccept}
                onReject={handleReject}
                onSelectAnotherEntity={handleSelectAnotherEntity}
            />
        );
    }

    return (
        <Paper
            sx={{
                p: 1.25,
                cursor: canNavigate ? 'pointer' : 'default',
                '&:hover': canNavigate ? {outline: '1px solid', outlineColor: 'primary.main'} : {},
                transition: 'background-color 0.2s',
                opacity: busy ? 0.75 : 1,
            }}
            onClick={() => onNavigate?.(task)}
        >
            {isCompliance ? (
                <>
                    <ComplianceCheckTaskCard task={task}/>
                    <ComplianceCheckTaskButtons
                        task={task}
                        blockNode={null}
                        busy={busy}
                        disabledReason={disabledReason}
                        onReject={handleReject}
                        onAccept={handleAcceptCompliance}
                        onInsertText={handleInsertText}
                    />
                </>
            ) : (
                <>
                    {task?.taskType === 'reference_analysis'
                        ? <StatementTaskCard task={task}/>
                        : <DefaultTaskCard task={task}/>}
                    <DefaultTaskButtons
                        busy={busy}
                        disabledReason={disabledReason}
                        onReject={handleReject}
                        onAccept={handleAccept}
                    />
                </>
            )}
        </Paper>
    );
}

